import { usePreviewNavState } from '@shared/ui/preview-link';

import styles from './issue-cover.module.css';
import { useT } from '@shared/i18n';

/**
 * `IssueCover`'s stand-in while the issue is still loading -- same box,
 * same spot, same `view-transition-name`, drawn from the navigation state
 * alone (`PreviewLink`'s payload carries the cover's `imageSrc`), since
 * there's no issue data yet to read it from.
 *
 * Exists for the cover-morph: a view transition takes its "after" snapshot
 * on the first frame of the new route, which on a first visit (cold issue
 * query, or the reader's lazy chunk still in flight) is the loading
 * skeleton, not the reader. Without an element carrying the name there,
 * the morph has no target and silently degrades to a cross-fade. The real
 * `IssueCover` then takes over in place once the reader mounts.
 *
 * Deliberately its own module with no issue/reader imports: the issue
 * routes' `pendingComponent` (`app/routing/route-catalog.tsx`) imports the
 * skeleton eagerly, so this must stay as cheap as the skeleton itself.
 *
 * A *disabled* button, not a decorative image: it looks exactly like the
 * real cover (any visual change would show up in the morph and again at
 * the hand-off), but can't be pressed and is announced as unavailable --
 * there's no issue to go back from yet. It's enabled by being replaced:
 * the real, interactive `IssueCover` only mounts once the issue has loaded
 * and the reader has rendered, and never if loading fails.
 */
export function IssueCoverPlaceholder() {
	const t = useT();
	const navState = usePreviewNavState();

	if (!navState?.imageSrc) {
		return null;
	}

	return (
		<button
			type="button"
			className={styles['issue-cover']}
			disabled
			title={t('reader.loadingIssue')}
		>
			<img
				src={navState.imageSrc}
				alt={t('reader.cover')}
				style={{ viewTransitionName: navState.transitionName }}
			/>
		</button>
	);
}
